import React, { useState } from 'react';
import { signup, getDeviceFingerprint } from './api';

const SignupForm = ({ onSignedUp, onBack }) => {
  const [form, setForm] = useState({
    username: '', firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
    secretQuestion1: '', secretAnswer1: '',
    secretQuestion2: '', secretAnswer2: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.username.trim() || form.username.trim().length < 3) {
      setError('Username must be at least 3 characters.'); return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(form.username.trim())) {
      setError('Username can only contain letters, numbers, and underscores.'); return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Please enter your full legal name.'); return;
    }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!form.secretQuestion1.trim() || !form.secretAnswer1.trim() ||
        !form.secretQuestion2.trim() || !form.secretAnswer2.trim()) {
      setError('Please fill in both security questions and answers.'); return;
    }
    if (form.secretQuestion1.trim() === form.secretQuestion2.trim()) {
      setError('Please use two different security questions.'); return;
    }
    setLoading(true);
    try {
      const result = await signup({
        username: form.username.trim(),
        email: form.email,
        password: form.password,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        secret_question_1: form.secretQuestion1.trim(),
        secret_answer_1: form.secretAnswer1.trim(),
        secret_question_2: form.secretQuestion2.trim(),
        secret_answer_2: form.secretAnswer2.trim(),
        deviceFingerprint: getDeviceFingerprint(),
      });
      onSignedUp(result, form.email);
    } catch (err) {
      const msg = err.message;
      try {
        const parsed = JSON.parse(msg);
        if (Array.isArray(parsed)) {
          setError(parsed.map(e => e.msg || JSON.stringify(e)).join(', '));
        } else {
          setError(msg);
        }
      } catch {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', background: 'rgba(5,5,8,0.6)',
    border: '1px solid #222230', borderRadius: '6px', color: '#ffffff',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', marginBottom: '6px', color: '#d1d1e0', fontSize: '13px', fontWeight: '500' };
  const fieldStyle = { marginBottom: '16px' };

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      minHeight: '100vh', backgroundColor: '#050508', padding: '40px 20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '480px', background: 'rgba(17,17,25,0.75)',
        backdropFilter: 'blur(16px)', border: '1px solid #222230',
        borderRadius: '10px', padding: '36px', boxShadow: '0 24px 64px rgba(0,0,0,0.9)',
      }}>
        <h1 style={{ margin: '0 0 6px 0', fontSize: '22px', fontWeight: '700', color: '#ffffff' }}>
          Create Account
        </h1>
        <p style={{ margin: '0 0 24px 0', fontSize: '13px', color: '#8a8a9e', lineHeight: '1.5' }}>
          Your account works immediately — we'll email you a code to verify your address.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Username</label>
            <input type="text" value={form.username} onChange={set('username')} required style={inputStyle}
              placeholder="Choose a unique username"
              onFocus={(e) => e.target.style.borderColor = '#a336ff'}
              onBlur={(e) => e.target.style.borderColor = '#222230'} />
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>First Name</label>
              <input type="text" value={form.firstName} onChange={set('firstName')} required style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#a336ff'}
                onBlur={(e) => e.target.style.borderColor = '#222230'} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Last Name</label>
              <input type="text" value={form.lastName} onChange={set('lastName')} required style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#a336ff'}
                onBlur={(e) => e.target.style.borderColor = '#222230'} />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Email</label>
            <input type="email" value={form.email} onChange={set('email')} required style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#a336ff'}
              onBlur={(e) => e.target.style.borderColor = '#222230'} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Password</label>
            <input type="password" value={form.password} onChange={set('password')} required style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#a336ff'}
              onBlur={(e) => e.target.style.borderColor = '#222230'} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Confirm Password</label>
            <input type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#a336ff'}
              onBlur={(e) => e.target.style.borderColor = '#222230'} />
            {form.confirmPassword.length > 0 && (
              <div style={{ fontSize: '12px', marginTop: '6px', fontFamily: 'monospace',
                color: form.password === form.confirmPassword ? '#00ff88' : '#ff4466' }}>
                {form.password === form.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #222230', margin: '20px 0', paddingTop: '20px' }}>
            <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#8a8a9e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Security Questions — for password recovery
            </p>

            <div style={fieldStyle}>
              <label style={labelStyle}>Security Question 1</label>
              <input type="text" value={form.secretQuestion1} onChange={set('secretQuestion1')}
                placeholder="e.g. What was your first pet's name?" required style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#a336ff'}
                onBlur={(e) => e.target.style.borderColor = '#222230'} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Answer 1</label>
              <input type="text" value={form.secretAnswer1} onChange={set('secretAnswer1')} required style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#a336ff'}
                onBlur={(e) => e.target.style.borderColor = '#222230'} />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Security Question 2</label>
              <input type="text" value={form.secretQuestion2} onChange={set('secretQuestion2')}
                placeholder="e.g. What city were you born in?" required style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#a336ff'}
                onBlur={(e) => e.target.style.borderColor = '#222230'} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Answer 2</label>
              <input type="text" value={form.secretAnswer2} onChange={set('secretAnswer2')} required style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#a336ff'}
                onBlur={(e) => e.target.style.borderColor = '#222230'} />
            </div>
          </div>

          {/* ⚠ DEVICE LOCK DISCLAIMER */}
          <div style={{
            border: '1px solid rgba(163,54,255,0.35)',
            borderLeft: '3px solid #a336ff',
            background: 'rgba(163,54,255,0.06)',
            borderRadius: '6px',
            padding: '12px 14px',
            marginBottom: '16px',
          }}>
            <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontFamily: 'monospace',
              color: '#a336ff', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              ⟫ Email Verification
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#b0b0c8', lineHeight: '1.6' }}>
              A verification code will be sent to your email from{' '}
              <span style={{ color: '#e0c0ff', fontWeight: '600' }}>admin@aetheris.fyi</span>.
              You can start using Aetheris right away and verify when the code arrives.
            </p>
          </div>

          {error && (
            <p style={{ color: '#ff4466', fontSize: '13px', background: 'rgba(255,68,102,0.08)',
              padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(255,68,102,0.2)',
              marginBottom: '16px' }}>{error}</p>
          )}

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '12px', background: 'linear-gradient(135deg, #a336ff 0%, #791fd9 100%)',
            border: '1px solid #cc88ff', borderRadius: '6px', color: '#ffffff',
            fontWeight: '600', fontSize: '14px', cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p style={{ marginTop: '20px', textAlign: 'center', fontSize: '13px', color: '#525266' }}>
          Already have an account?{' '}
          <button type="button" onClick={onBack} style={{
            background: 'none', border: 'none', color: '#a336ff',
            cursor: 'pointer', fontWeight: '600', padding: 0,
          }}>Log in</button>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
